import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { PasswordModule } from 'primeng/password';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AdminService } from '../../services/admin.service';
import { AdminFeatureManagementData, AdminFeaturePermission, AdminFeatureSection, RoleManagementData, RoleManagementPage, RoleManagementPermissionRole, RoleManagementRole, SystemRole, UserTypeItem } from '../../models/role-management.model';
import { ProjectStateService } from '../../services/project-state.service';

@Component({
  selector: 'app-role-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CheckboxModule,
    DialogModule,
    PasswordModule,
    ToastModule
  ],
  templateUrl: './role-management.html',
  styleUrl: './role-management.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoleManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ── View switch ──
  activeView: 'project' | 'application' = 'project';

  setView(view: 'project' | 'application'): void {
    this.activeView = view;
  }

  // ── Project-level permissions ──
  loading = false;
  saving = false;
  data: RoleManagementData | null = null;
  roles: RoleManagementRole[] = [];
  pages: RoleManagementPage[] = [];
  filteredPages: RoleManagementPage[] = [];
  permissionMap: Record<number, Record<number, boolean>> = {};
  showPassKeyDialog = false;
  roleManagementPassKey = '';
  passKeyError = '';
  validatingPassKey = false;
  pendingSaveTarget: 'project' | 'application' | null = null;
  pendingPermissionPayload: RoleManagementPermissionRole[] = [];
  pendingAdminFeaturePayload: AdminFeaturePermission[] = [];

  // ── Application-level feature permissions ──
  adminLoading = false;
  adminSaving = false;
  adminData: AdminFeatureManagementData | null = null;
  adminSections: AdminFeatureSection[] = [];
  systemRoles: SystemRole[] = [];
  userTypes: UserTypeItem[] = [];
  adminPermissionMap: Record<number, Record<number, Record<number, boolean>>> = {};

  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private projectStateService: ProjectStateService
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.loadAdminFeatureData();
    this.projectStateService.pageSearch$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(search => {
      this.applySearch(search);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Project-level: search ──

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    this.filteredPages = term
      ? this.pages.filter(p => (p.displayName || '').toLowerCase().includes(term) || (p.pageKey || '').toLowerCase().includes(term))
      : [...this.pages];
    this.cdr.markForCheck();
  }

  // ── Project-level: load & permission map ──

  private loadData(): void {
    this.loading = true;
    this.adminService.getRoleManagementData().subscribe({
      next: (data) => {
        this.data = data;
        this.roles = data.roles || [];
        this.pages = data.pages || [];
        this.buildPermissionMap(data.permissionRoles || []);
        this.applySearch(this.projectStateService.getPageSearch());
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load role permissions' });
        this.cdr.markForCheck();
      }
    });
  }

  private buildPermissionMap(permissionRoles: RoleManagementPermissionRole[]): void {
    const map: Record<number, Record<number, boolean>> = {};
    this.pages.forEach(page => {
      page.permissions?.forEach(permission => {
        if (!map[permission.permissionId]) map[permission.permissionId] = {};
        this.roles.forEach(role => { map[permission.permissionId][role.roleId] = false; });
      });
    });
    permissionRoles.forEach(item => {
      if (!map[item.permissionId]) map[item.permissionId] = {};
      map[item.permissionId][item.projectRoleId] = item.isAllowed;
    });
    this.permissionMap = map;
  }

  isAllowed(permissionId: number, roleId: number): boolean {
    return this.permissionMap[permissionId]?.[roleId] ?? false;
  }

  isPageRoleChecked(page: RoleManagementPage, roleId: number): boolean {
    if (!page.permissions?.length) return false;
    return page.permissions.every(p => this.isAllowed(p.permissionId, roleId));
  }

  isPageRoleIndeterminate(page: RoleManagementPage, roleId: number): boolean {
    if (!page.permissions?.length) return false;
    const count = page.permissions.filter(p => this.isAllowed(p.permissionId, roleId)).length;
    return count > 0 && count < page.permissions.length;
  }

  togglePageRolePermissions(page: RoleManagementPage, roleId: number, value: boolean): void {
    page.permissions?.forEach(p => this.togglePermission(p.permissionId, roleId, value));
  }

  togglePermission(permissionId: number, roleId: number, value: boolean): void {
    if (!this.permissionMap[permissionId]) this.permissionMap[permissionId] = {};
    this.permissionMap[permissionId][roleId] = value;
  }

  savePermissions(): void {
    if (!this.data) return;
    const payload: RoleManagementPermissionRole[] = [];
    this.pages.forEach(page => {
      page.permissions?.forEach(permission => {
        this.roles.forEach(role => {
          payload.push({ permissionId: permission.permissionId, projectRoleId: role.roleId, isAllowed: this.isAllowed(permission.permissionId, role.roleId) });
        });
      });
    });

    this.pendingSaveTarget = 'project';
    this.pendingPermissionPayload = payload;
    this.pendingAdminFeaturePayload = [];
    this.roleManagementPassKey = '';
    this.passKeyError = '';
    this.showPassKeyDialog = true;
    this.cdr.markForCheck();
  }

  saveAdminFeaturePermissions(): void {
    if (!this.adminData) return;
    const payload: AdminFeaturePermission[] = [];
    this.adminSections.forEach(section => {
      section.features?.forEach(feature => {
        this.systemRoles.forEach(role => {
          this.userTypes.forEach(ut => {
            payload.push({ featureId: feature.featureId, roleId: role.roleId, userTypeId: ut.userTypeId, isAllowed: this.isAdminFeatureAllowed(feature.featureId, role.roleId, ut.userTypeId) });
          });
        });
      });
    });

    this.pendingSaveTarget = 'application';
    this.pendingPermissionPayload = [];
    this.pendingAdminFeaturePayload = payload;
    this.roleManagementPassKey = '';
    this.passKeyError = '';
    this.showPassKeyDialog = true;
    this.cdr.markForCheck();
  }

  closePassKeyDialog(): void {
    if (this.validatingPassKey || this.saving || this.adminSaving) {
      return;
    }

    this.showPassKeyDialog = false;
    this.roleManagementPassKey = '';
    this.passKeyError = '';
    this.pendingSaveTarget = null;
    this.pendingPermissionPayload = [];
    this.pendingAdminFeaturePayload = [];
    this.cdr.markForCheck();
  }

  confirmPassKeyAndSave(): void {
    const normalizedPassKey = (this.roleManagementPassKey || '').trim();
    if (normalizedPassKey.length !== 6) {
      this.passKeyError = 'Passkey must be exactly 6 characters.';
      this.cdr.markForCheck();
      return;
    }

    this.validatingPassKey = true;
    this.passKeyError = '';

    this.adminService.validateRoleManagementPassKey({ passKey: normalizedPassKey }).subscribe({
      next: (result) => {
        if (!result?.isValid) {
          this.validatingPassKey = false;
          this.passKeyError = 'Invalid passkey. Please try again.';
          this.cdr.markForCheck();
          return;
        }

        if (this.pendingSaveTarget === 'application') {
          this.executeSaveAdminFeaturePermissions(normalizedPassKey);
          return;
        }

        this.executeSavePermissions(normalizedPassKey);
      },
      error: () => {
        this.validatingPassKey = false;
        this.passKeyError = 'Unable to validate passkey right now.';
        this.cdr.markForCheck();
      }
    });
  }

  private executeSavePermissions(passKey: string): void {
    this.saving = true;
    this.adminService.saveRoleManagementPermissions({
      permissionRoles: this.pendingPermissionPayload,
      passKey
    }).subscribe({
      next: () => {
        this.saving = false;
        this.validatingPassKey = false;
        this.showPassKeyDialog = false;
        this.roleManagementPassKey = '';
        this.passKeyError = '';
        this.pendingSaveTarget = null;
        this.pendingPermissionPayload = [];
        this.pendingAdminFeaturePayload = [];
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Permissions saved successfully' });
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving = false;
        this.validatingPassKey = false;
        this.passKeyError = 'Failed to save permissions. Please verify the passkey and try again.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save permissions' });
        this.cdr.markForCheck();
      }
    });
  }

  // ── Application-level: load & permission map ──

  private loadAdminFeatureData(): void {
    this.adminLoading = true;
    this.adminService.getAdminFeatureManagementData().subscribe({
      next: (data) => {
        this.adminData = data;
        this.adminSections = data.sections || [];
        this.systemRoles = data.systemRoles || [];
        this.userTypes = data.userTypes || [];
        this.buildAdminPermissionMap(data.featurePermissions || []);
        this.adminLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.adminLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load application feature permissions' });
        this.cdr.markForCheck();
      }
    });
  }

  private buildAdminPermissionMap(featurePermissions: AdminFeaturePermission[]): void {
    const map: Record<number, Record<number, Record<number, boolean>>> = {};
    this.adminSections.forEach(section => {
      section.features?.forEach(feature => {
        if (!map[feature.featureId]) map[feature.featureId] = {};
        this.systemRoles.forEach(role => {
          if (!map[feature.featureId][role.roleId]) map[feature.featureId][role.roleId] = {};
          this.userTypes.forEach(ut => { map[feature.featureId][role.roleId][ut.userTypeId] = false; });
        });
      });
    });
    featurePermissions.forEach(item => {
      if (!map[item.featureId]) map[item.featureId] = {};
      if (!map[item.featureId][item.roleId]) map[item.featureId][item.roleId] = {};
      map[item.featureId][item.roleId][item.userTypeId] = item.isAllowed;
    });
    this.adminPermissionMap = map;
  }

  isAdminFeatureAllowed(featureId: number, roleId: number, userTypeId: number): boolean {
    return this.adminPermissionMap[featureId]?.[roleId]?.[userTypeId] ?? false;
  }

  isSectionRoleTypeChecked(section: AdminFeatureSection, roleId: number, userTypeId: number): boolean {
    if (!section.features?.length) return false;
    return section.features.every(f => this.isAdminFeatureAllowed(f.featureId, roleId, userTypeId));
  }

  isSectionRoleTypeIndeterminate(section: AdminFeatureSection, roleId: number, userTypeId: number): boolean {
    if (!section.features?.length) return false;
    const count = section.features.filter(f => this.isAdminFeatureAllowed(f.featureId, roleId, userTypeId)).length;
    return count > 0 && count < section.features.length;
  }

  toggleSectionRoleType(section: AdminFeatureSection, roleId: number, userTypeId: number, value: boolean): void {
    section.features?.forEach(f => this.toggleAdminFeature(f.featureId, roleId, userTypeId, value));
  }

  toggleAdminFeature(featureId: number, roleId: number, userTypeId: number, value: boolean): void {
    if (!this.adminPermissionMap[featureId]) this.adminPermissionMap[featureId] = {};
    if (!this.adminPermissionMap[featureId][roleId]) this.adminPermissionMap[featureId][roleId] = {};
    this.adminPermissionMap[featureId][roleId][userTypeId] = value;
  }

  private executeSaveAdminFeaturePermissions(passKey: string): void {
    this.adminSaving = true;
    this.adminService.saveAdminFeaturePermissions({
      featurePermissions: this.pendingAdminFeaturePayload,
      passKey
    }).subscribe({
      next: () => {
        this.adminSaving = false;
        this.validatingPassKey = false;
        this.showPassKeyDialog = false;
        this.roleManagementPassKey = '';
        this.passKeyError = '';
        this.pendingSaveTarget = null;
        this.pendingPermissionPayload = [];
        this.pendingAdminFeaturePayload = [];
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Application permissions saved successfully' });
        this.cdr.markForCheck();
      },
      error: () => {
        this.adminSaving = false;
        this.validatingPassKey = false;
        this.passKeyError = 'Failed to save permissions. Please verify the passkey and try again.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save application permissions' });
        this.cdr.markForCheck();
      }
    });
  }
}
